import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Loader2, MailPlus, UserRound, Handshake, CheckCircle2, X } from 'lucide-react';
import { Button } from '../ui-v2';
import { Input, Select, Chips, Badge, Drawer } from '../ui-v2.5';
import { crearSolicitud } from './api';
import CopiarEnlace from './components/CopiarEnlace';
import { fmtFecha } from './fecha';
import { activosDelAmbito, INTERNO } from './pertenencia';

/**
 * SolicitarDatosDrawer — drawer para enviar la solicitud de datos al candidato (Fase 8).
 *
 * Datos del candidato + empresa/puesto + destinatarios de aviso (chips) + caducidad + vista previa
 * del correo. Al enviar, pasa a un estado de ÉXITO con el enlace y "Copiar enlace" (única vez que el
 * enlace existe). No se envía ningún correo desde aquí: el backend compone y (en dev) sólo lo registra.
 */

const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const esEmail = (v) => typeof v === 'string' && RE_EMAIL.test(v.trim());
const cuerpoError = (e) => (e && e.response && e.response.data) || {};
const DIAS_OPC = [3, 7, 14, 30];
const TIPO_LABEL = { interno: 'Interno', colaborador: 'Colaborador' };

// Estimación de caducidad para la vista previa (hoy + N días). El valor real lo fija el backend;
// aquí solo se muestra, formateado con la zona del proyecto (dd/mm/aaaa, Europe/Madrid).
function fechaCaducidad(dias) {
  const d = new Date();
  d.setDate(d.getDate() + Number(dias || 7));
  return fmtFecha(d, '');
}

export default function SolicitarDatosDrawer({ open, onClose, tipo = 'interno', negocios = [], puestos = [], catalogosError = false, onReintentarCatalogos, onCreada }) {
  const [nombre, setNombre] = useState('');
  const [apellidos, setApellidos] = useState('');
  const [email, setEmail] = useState('');
  const [negocioId, setNegocioId] = useState('');
  const [puestoId, setPuestoId] = useState('');
  const [avisos, setAvisos] = useState([]);
  const [dias, setDias] = useState(7);
  const [errores, setErrores] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [exito, setExito] = useState(null); // { enlace }

  const puestosDelNegocio = useMemo(
    /* Solo puestos de INTERNO: un aviso cuelga de un negocio (`incorp_reglas_aviso.negocio_id`) y
     * un puesto de colaborador no tiene negocio. Antes esto salia bien por accidente -su
     * `negocio_id` a NULL no casaba con ningun id-; ahora se dice, porque una regla que se cumple
     * sola deja de cumplirse el dia que alguien toca lo de al lado. */
    () => activosDelAmbito(puestos, { tipo: INTERNO, negocioId }),
    [puestos, negocioId],
  );
  const empresaNombre = useMemo(
    () => (negocios.find((n) => n.id === Number(negocioId)) || {}).nombre || '',
    [negocios, negocioId],
  );
  const puestoNombre = useMemo(
    () => (puestosDelNegocio.find((p) => p.id === Number(puestoId)) || {}).nombre || '',
    [puestosDelNegocio, puestoId],
  );

  function cambiarNegocio(v) { setNegocioId(v); setPuestoId(''); }

  function validar() {
    const err = {};
    if (!nombre.trim()) err.nombre = 'El nombre es obligatorio.';
    else if (nombre.trim().length > 150) err.nombre = 'Máximo 150 caracteres.';
    if (!apellidos.trim()) err.apellidos = 'Los apellidos son obligatorios.';
    else if (apellidos.trim().length > 150) err.apellidos = 'Máximo 150 caracteres.';
    if (!esEmail(email)) err.email = 'Email no válido.';
    if (!negocioId) err.negocio = 'Selecciona la empresa.';
    const avisosMal = avisos.filter((a) => !esEmail(a));
    if (avisosMal.length) err.avisos = `Correos de aviso no válidos: ${avisosMal.join(', ')}`;
    setErrores(err);
    if (Object.keys(err).length) return null;
    return {
      tipo_persona: tipo,
      nombre_candidato: nombre.trim(),
      apellidos_candidato: apellidos.trim(),
      email_candidato: email.trim(),
      negocio_id: Number(negocioId),
      puesto_id: puestoId ? Number(puestoId) : null,
      avisos: avisos.map((a) => a.trim()),
      dias_validez: Number(dias),
    };
  }

  async function enviar() {
    const payload = validar();
    if (!payload) return;
    setEnviando(true);
    try {
      const res = await crearSolicitud(payload);
      toast.success('Solicitud creada. El correo se ha compuesto para el candidato.');
      setExito({ enlace: res.enlace });
      if (onCreada) onCreada();
    } catch (e) {
      const body = cuerpoError(e);
      toast.error(body.message || 'No se pudo crear la solicitud.');
    } finally {
      setEnviando(false);
    }
  }

  function reset() {
    // `tipo` no se resetea: lo fija la pestaña activa (prop), no el drawer.
    setNombre(''); setApellidos(''); setEmail(''); setNegocioId(''); setPuestoId('');
    setAvisos([]); setDias(7); setErrores({}); setExito(null);
  }
  function cerrar() { reset(); onClose(); }

  const header = (
    <div className="flex items-center justify-between border-b border-v25-line px-5 py-4">
      <div className="flex items-center gap-2">
        <MailPlus className="h-5 w-5 text-v25-brand-700" />
        <div>
          <h2 className="text-sm font-semibold text-v25-ink-900">Solicitar datos al candidato</h2>
          <p className="text-xs text-v25-ink-500">Se enviará un enlace privado para que complete sus datos.</p>
        </div>
      </div>
      <button type="button" onClick={cerrar} className="text-v25-ink-400 hover:text-v25-ink-700" aria-label="Cerrar"><X className="h-5 w-5" /></button>
    </div>
  );

  const footer = exito ? (
    <div className="flex items-center justify-end gap-2 border-t border-v25-line px-5 py-4">
      <Button variant="outline" onClick={() => { reset(); }}>Nueva solicitud</Button>
      <Button onClick={cerrar}>Hecho</Button>
    </div>
  ) : (
    <div className="flex items-center justify-end gap-2 border-t border-v25-line px-5 py-4">
      <Button variant="outline" onClick={cerrar} disabled={enviando}>Cancelar</Button>
      <Button onClick={enviar} disabled={enviando}>
        {enviando ? <Loader2 className="animate-spin" /> : <MailPlus />}Enviar solicitud
      </Button>
    </div>
  );

  return (
    <Drawer open={open} onClose={cerrar} width={620} header={header} footer={footer}>
      {exito ? (
        <div className="space-y-4 p-5">
          <div className="flex items-start gap-3 rounded-v25-md border border-v25-received-bd bg-v25-received-bg p-4">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-v25-received-fg" />
            <div>
              <p className="text-sm font-medium text-v25-received-fg">Solicitud creada para {[nombre.trim(), apellidos.trim()].filter(Boolean).join(' ')}</p>
              <p className="mt-0.5 text-xs text-v25-ink-600">Se ha compuesto el correo con el enlace. Caduca el {fechaCaducidad(dias)}.</p>
            </div>
          </div>
          <CopiarEnlace enlace={exito.enlace} />
        </div>
      ) : (
        <div className="space-y-5 p-5">
          {/* Candidato */}
          <section className="space-y-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Candidato</h4>
            {/* El tipo lo fija la pestaña activa del listado: aquí se muestra, no se elige. */}
            <div>
              <label className="mb-1 block text-xs font-medium text-v25-ink-700">Tipo</label>
              <div className="flex items-center gap-2 rounded-v25-md border border-v25-line bg-v25-surface-sunken px-3 py-2">
                {tipo === 'interno' ? <UserRound className="h-4 w-4 text-v25-ink-500" /> : <Handshake className="h-4 w-4 text-v25-ink-500" />}
                <span className="text-sm text-v25-ink-800">{TIPO_LABEL[tipo]}</span>
                <span className="ml-auto text-[11px] text-v25-ink-400">según la pestaña activa</span>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input label="Nombre *" value={nombre} onChange={(e) => setNombre(e.target.value)} error={errores.nombre} maxLength={150} />
              <Input label="Apellidos *" value={apellidos} onChange={(e) => setApellidos(e.target.value)} error={errores.apellidos} maxLength={150} />
            </div>
            <Input label="Email *" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={errores.email} placeholder="candidato@correo.com" />
          </section>

          {/* Empresa y puesto */}
          <section className="space-y-3 border-t border-v25-line pt-4">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Empresa y puesto</h4>
            {catalogosError && (
              <div className="flex items-center justify-between gap-2 rounded-v25-md border border-v25-expired-bd bg-v25-expired-bg px-3 py-2 text-xs text-v25-expired-fg">
                <span>No se pudieron cargar las empresas y puestos.</span>
                {onReintentarCatalogos && (
                  <button type="button" onClick={onReintentarCatalogos} className="shrink-0 font-medium underline">Reintentar</button>
                )}
              </div>
            )}
            <Select label="Negocio *" value={negocioId} onChange={(e) => cambiarNegocio(e.target.value)} error={errores.negocio}>
              <option value="">Selecciona una empresa…</option>
              {negocios.map((n) => <option key={n.id} value={n.id}>{n.nombre}</option>)}
            </Select>
            <Select label="Puesto (opcional)" value={puestoId} onChange={(e) => setPuestoId(e.target.value)} disabled={!negocioId}
              hint={negocioId && puestosDelNegocio.length === 0 ? 'Esta empresa no tiene puestos activos.' : undefined}>
              <option value="">Sin puesto concreto</option>
              {puestosDelNegocio.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </Select>
          </section>

          {/* Avisos y caducidad */}
          <section className="space-y-3 border-t border-v25-line pt-4">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Aviso de recepción</h4>
            <div>
              <label className="mb-1 block text-xs font-medium text-v25-ink-700">Destinatarios del aviso (opcional)</label>
              <Chips value={avisos} onChange={setAvisos} input placeholder="Escribe un email y pulsa Enter" />
              {errores.avisos ? <p className="mt-1 text-xs text-red-600">{errores.avisos}</p>
                : <p className="mt-1 text-[11px] text-v25-ink-400">Recibirán un aviso cuando el candidato envíe sus datos.</p>}
            </div>
            <Select label="Caducidad del enlace" value={dias} onChange={(e) => setDias(e.target.value)}>
              {DIAS_OPC.map((d) => <option key={d} value={d}>{d} días</option>)}
            </Select>
          </section>

          {/* Vista previa del correo */}
          <section className="space-y-2 border-t border-v25-line pt-4">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-v25-ink-500">Vista previa del correo</h4>
            <div className="rounded-v25-md border border-v25-line bg-white p-3 text-sm text-v25-ink-700">
              <p className="mb-2 text-xs text-v25-ink-500"><strong>Para:</strong> {email || '—'}</p>
              <p className="mb-2 text-xs text-v25-ink-500"><strong>Asunto:</strong> Completa tus datos de incorporación · {empresaNombre || 'GRUPO LAE'}</p>
              <div className="border-t border-v25-line pt-2">
                <p className="mb-1">Hola {nombre.trim() || '…'},</p>
                <p className="mb-1">Desde <strong>{empresaNombre || '…'}</strong> necesitamos que completes tus datos para tu incorporación{puestoNombre ? <> para el puesto <strong>{puestoNombre}</strong></> : null}.</p>
                <p className="mb-2"><Badge tone="info">Completar mis datos</Badge> <span className="text-xs text-v25-ink-400">(enlace privado)</span></p>
                <p className="text-xs text-v25-expired-fg">El enlace caduca el {fechaCaducidad(dias)}.</p>
              </div>
            </div>
          </section>
        </div>
      )}
    </Drawer>
  );
}
